/**
 * The audio table (Dax, 2026-10-04): the library's sounds as compact rows, a hundred to a page, for
 * Browse's table view and the Library's Audio view. A row plays from its button; its name renames in place
 * (the brand's own sounds). Sound effects show the nine sound categories as columns, music its artist,
 * genres, moods and trending, voiceovers their voice; every header sorts and a second click flips it.
 * Columns run waveform, name, length, rating, used, tags, then the route's own, pack, publisher and import
 * date (2026-10-04); a long name is cut short with the whole on hover, and a row's tags show nine at a time
 * with More for the rest; their pencil opens them as pills, each renamed or removed on its own, a new one
 * typed into the empty pill, every change saved as it is made. Clicking a waveform plays it, as its button does. A category cell's pencil picks its values (a person's choice, which the automatic filing never
 * overrides); with several rows ticked, the pick applies to all of them. Each row carries its rating, its
 * pack, how often approved posts used it, and the menu the cards have.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AUDIO_DESCRIBE_SETTLE_MS, AUDIO_NAME_MAX, AUDIO_TABLE_NAME_CHARS, AUDIO_TABLE_TAGS_SHOWN, AUDIO_TAG_MAX_CHARS, AUDIO_TAGS_MAX, MUSIC_GENRES, MUSIC_MOODS, type AudioKind, type MusicGenre, type MusicMood, type SfxCategory, type SfxSetting } from '@studio/contracts';
import { AudioDetails, type AudioDetailsValue } from '@/components/audio-details';
import { useAudition } from '@/components/audio-card';
import { Icon } from '@/components/icons';
import { Pager } from '@/components/pager';
import { PillList } from '@/components/pill-editor';
import { RowMenu } from '@/components/row-menu';
import { StarRating } from '@/components/star-rating';
import { displayGain } from '@/components/waveform';
import { useRatings } from '@/hooks/use-ratings';
import { fmtTime, fmtWhen } from '@/lib/format';
import { queryClient, trpc } from '@/lib/trpc';

export type AudioRouteFilter = 'music' | 'sfx' | 'voice' | null;
type Row = NonNullable<ReturnType<typeof useTable>['data']>['rows'][number];
/** The categories offered as filters above a sound-effect table: the ones a sound is most often found by. */
const FILTER_CATEGORY_KEYS = ['sound_world', 'sound_action', 'sound_material', 'sound_feel'];
/** Text reads A to Z first; numbers and dates biggest and newest first. */
const TEXT_SORTS = new Set(['name', 'pack', 'publisher']);

/** A long name cut short at a whole word, keeping a series' number at its end so "… 03" still tells the takes apart. */
export function shortName(name: string, max = AUDIO_TABLE_NAME_CHARS): string {
  if (name.length <= max) return name;
  const tail = / \d+$/.exec(name)?.[0] ?? '';
  const head = name.slice(0, max - 1 - tail.length);
  // Back to the last whole word, unless that would lose most of the name.
  const space = head.lastIndexOf(' ');
  return `${(space > head.length / 2 ? head.slice(0, space) : head).replace(/[\s,]+$/, '')}…${tail}`;
}


function useTable(input: { route: AudioRouteFilter; q: string | null; pack: string | null; values: Record<string, string>; sort: string; dir: 'asc' | 'desc'; page: number }) {
  return useQuery(trpc.sounds.table.queryOptions(input, { placeholderData: (p) => p }));
}

function cssVar(el: HTMLElement, name: string, fallback: string): string {
  return getComputedStyle(el).getPropertyValue(name).trim() || fallback;
}

/** A row's waveform at text height: the sound's display peaks, spread evenly. */
function RowWave({ peaks, label, onPlay }: { peaks: number[]; label: string; onPlay: () => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx || !w || !h) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = cssVar(canvas, '--wf-sfx', '#a85a1f');
    const bars = Math.floor(w / 2);
    const gain = displayGain(peaks, 0, peaks.length);
    for (let b = 0; b < bars; b++) {
      const i0 = Math.floor((b / bars) * peaks.length);
      const i1 = Math.max(i0 + 1, Math.floor(((b + 1) / bars) * peaks.length));
      let p = 0;
      for (let i = i0; i < i1; i++) p = Math.max(p, peaks[i] ?? 0);
      const bh = Math.max(1, Math.min(1, p * gain) * h);
      ctx.fillRect(b * 2, (h - bh) / 2, 1, bh);
    }
  }, [peaks]);
  return (
    <button type="button" className="row-wave-btn" aria-label={label} onClick={onPlay}>
      <canvas ref={ref} className="row-wave" aria-hidden="true" />
    </button>
  );
}

/** Picking a cell's values: one sound, or every ticked one. */
function PickValues({ title, options, initial, max, onApply, onClose }: {
  title: string;
  options: { id: string; label: string }[];
  initial: string[];
  max?: number;
  onApply: (ids: string[]) => void;
  onClose: () => void;
}) {
  const [chosen, setChosen] = useState<Set<string>>(new Set(initial));
  const flip = (id: string) => setChosen((c) => {
    const next = new Set(c);
    if (next.has(id)) next.delete(id);
    else if (max === undefined || next.size < max) next.add(id);
    return next;
  });
  return (
    <>
      <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={onClose} />
      <div className="modal pick-modal" role="dialog" aria-label={title}>
        <h2>{title}</h2>
        <div className="row pick-values">
          {options.map((o) => (
            <button key={o.id} type="button" className={`chip pick-chip${chosen.has(o.id) ? ' on' : ''}`} aria-pressed={chosen.has(o.id)} onClick={() => flip(o.id)}>{o.label}</button>
          ))}
        </div>
        <div className="row between">
          <button type="button" className="btn quiet" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" onClick={() => onApply([...chosen])}>Apply</button>
        </div>
      </div>
    </>
  );
}

/**
 * A sound's tags as pills (2026-10-04): each renamed by its pencil or taken off by its cross, a new one typed
 * into the empty pill; every change saves at once, so the modal simply closes when the person is done.
 */
function TagsModal({ name, tags, onSave, onClose }: { name: string; tags: string[]; onSave: (tags: string[]) => void; onClose: () => void }) {
  const [list, setList] = useState(tags);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={onClose} />
      <div className="modal tags-modal" role="dialog" aria-label={`Tags: ${name}`}>
        <h2>Tags <span className="sub">{name}</span></h2>
        <PillList items={list} placeholder="New tag" label={`Add a tag to ${name}`} maxLength={AUDIO_TAG_MAX_CHARS} max={AUDIO_TAGS_MAX}
          onChange={(next) => {
            const clean = [...new Set(next.map((t) => t.trim().toLowerCase()).filter(Boolean))];
            setList(clean);
            onSave(clean);
          }} />
        <div className="row audio-details-foot">
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
}

export function AudioTable({ route, q, pack, onOpenPack }: {
  route: AudioRouteFilter;
  q: string | null;
  pack: string | null;
  onOpenPack: (packId: string) => void;
}) {
  const [sort, setSort] = useState('created');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [values, setValues] = useState<Record<string, string>>({});
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState<string | null>(null);
  const scope = JSON.stringify([route, q, pack, values, sort, dir]);
  useEffect(() => setPage(1), [scope]);
  useEffect(() => setPicked(new Set()), [route, q, pack]);
  const table = useTable({ route, q, pack, values, sort, dir, page });
  const data = table.data;
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const { ratingOf, rate } = useRatings(useMemo(() => rows.map((r) => ({ kind: 'audio_bed' as const, id: r.id })), [rows]));
  const { playingId, toggle, stop } = useAudition();
  useEffect(() => stop(), [scope, stop]);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: trpc.sounds.table.pathKey() });

  const setCategory = useMutation(trpc.sounds.setCategory.mutationOptions({
    onSuccess: (r) => { setNote(r.skipped ? `${r.skipped} not this brand's to change` : null); refresh(); },
    onError: (e) => setNote(e.message),
  }));
  const updateBed = useMutation(trpc.compose.updateBed.mutationOptions({ onSuccess: refresh, onError: (e) => setNote(e.message) }));
  const describeBeds = useMutation(trpc.compose.describeBeds.mutationOptions({
    onSuccess: (r) => { setNote(`Listening to ${r.queued}…`); window.setTimeout(refresh, AUDIO_DESCRIBE_SETTLE_MS); },
    onError: (e) => setNote(e.message),
  }));
  const removeBed = useMutation(trpc.compose.removeBed.mutationOptions({ onSuccess: refresh, onError: (e) => setNote(e.message) }));
  const [editing, setEditing] = useState<{ row: Row; facetId: string } | { row: Row; music: 'genres' | 'moods' } | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  // A row's tags: rows showing all of them, and the sound whose tags are open as pills.
  const [tagsOpen, setTagsOpen] = useState<Set<string>>(new Set());
  const [tagging, setTagging] = useState<Row | null>(null);
  const [details, setDetails] = useState<AudioDetailsValue | null>(null);

  const cats = data?.categories ?? [];
  const showCats = route === 'sfx';
  const header = (key: string, label: string, numeric = false) => (
    <th key={key} className={numeric ? 'n' : undefined}>
      <button type="button" className={`th-sort${sort === key ? ' on' : ''}`} onClick={() => {
        if (sort === key) setDir(dir === 'asc' ? 'desc' : 'asc');
        else { setSort(key); setDir(TEXT_SORTS.has(key) ? 'asc' : 'desc'); }
      }}>{label}{sort === key ? (dir === 'desc' ? ' ↓' : ' ↑') : ''}</button>
    </th>
  );
  /** The rows a cell edit applies to: every ticked row when this one is among them, else just this one. */
  const targetsOf = (row: Row) => (picked.has(row.id) && picked.size > 1 ? [...picked] : [row.id]);
  const commitName = () => {
    if (!renaming) return;
    const name = renaming.name.trim();
    const was = rows.find((r) => r.id === renaming.id)?.name;
    setRenaming(null);
    if (name && name !== was) updateBed.mutate({ bedId: renaming.id, name });
  };

  return (
    <div className="audio-table-wrap">
      {showCats && cats.length > 0 && (
        <div className="row audio-table-filters">
          {cats.filter((c) => FILTER_CATEGORY_KEYS.includes(c.key)).map((c) => (
            <label key={c.id} className="field browse-select">
              <select value={values[c.id] ?? ''} aria-label={c.label} onChange={(e) => setValues((v) => {
                const { [c.id]: gone, ...rest } = v;
                void gone;
                return e.target.value ? { ...rest, [c.id]: e.target.value } : rest;
              })}>
                <option value="">{c.label} · Any</option>
                {c.values.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
              </select>
            </label>
          ))}
        </div>
      )}
      <div className="row between audio-table-bar">
        <span className="sub">{picked.size ? `${picked.size} ticked: a category set on one of them sets it on all` : `${data?.total ?? 0} sounds`}{note ? ` · ${note}` : ''}</span>
        {data && <Pager page={page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
      </div>
      <div className="card audio-table-card">
        <table className="tbl audio-tbl">
          <thead>
            <tr>
              <th className="check-cell">
                <input type="checkbox" aria-label="Tick every sound on this page" checked={rows.length > 0 && rows.every((r) => picked.has(r.id))}
                  onChange={(e) => setPicked(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} />
              </th>
              <th aria-label="Play" />
              {header('name', 'Name')}
              {header('length', 'Length', true)}
              {header('rating', 'Rating')}
              {header('used', 'Used', true)}
              <th>Tags</th>
              {showCats && cats.map((c) => header(c.id, c.label))}
              {route === 'music' && <><th>Artist</th><th>Genre</th><th>Mood</th><th>Trending</th></>}
              {route === 'voice' && <th>Voice</th>}
              {route === null && <th>Kind</th>}
              {header('pack', 'Pack')}
              {route !== 'voice' && header('publisher', route === 'music' ? 'Label' : 'Publisher')}
              {header('created', 'Imported', true)}
              <th className="menu-cell" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={picked.has(r.id) ? 'lib-picked' : ''}>
                <td className="check-cell">
                  <input type="checkbox" aria-label={`Tick ${r.name}`} checked={picked.has(r.id)} onChange={() => setPicked((p) => {
                    const next = new Set(p);
                    if (next.has(r.id)) next.delete(r.id);
                    else next.add(r.id);
                    return next;
                  })} />
                </td>
                <td className="play-cell">
                  <span className="row play-cell-in">
                    <button type="button" className="pv-btn" aria-label={playingId === r.id ? `Stop ${r.name}` : `Play ${r.name}`} onClick={() => toggle(r.id, r.audioUrl)}>
                      <Icon name={playingId === r.id ? 'stop' : 'play'} />
                    </button>
                    <RowWave peaks={r.peaks} label={playingId === r.id ? `Stop ${r.name}` : `Play ${r.name}`} onPlay={() => toggle(r.id, r.audioUrl)} />
                  </span>
                </td>
                <td className="name-td" title={r.name.length > AUDIO_TABLE_NAME_CHARS ? [r.name, r.description].filter(Boolean).join('\n') : r.description ?? undefined}>
                  {renaming?.id === r.id ? (
                    <input className="res-name-input" autoFocus value={renaming.name} maxLength={AUDIO_NAME_MAX} aria-label={`Rename ${r.name}`}
                      onChange={(e) => setRenaming({ id: r.id, name: e.target.value })} onBlur={commitName}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitName(); if (e.key === 'Escape') setRenaming(null); }} />
                  ) : (
                    <span className="row name-cell">
                      <span className="audio-name">{shortName(r.name)}</span>
                      {r.mine && (
                        <button type="button" className="name-edit" title="Rename" aria-label={`Rename ${r.name}`} onClick={() => setRenaming({ id: r.id, name: r.name })}>
                          <Icon name="pencil" />
                        </button>
                      )}
                    </span>
                  )}
                </td>
                <td className="n mono">{fmtTime(r.durationMs, false)}</td>
                <td><StarRating rating={ratingOf({ kind: 'audio_bed', id: r.id })} onRate={(stars) => rate({ kind: 'audio_bed', id: r.id }, stars)} /></td>
                <td className="n mono">{r.used || ''}</td>
                <td className="tags-cell">
                  <span className="row tags-wrap">
                    {/* Nine slots: past nine tags, eight show and the ninth is More, which takes no slot of its own. */}
                    <span className="tags-grid">
                      {(() => {
                        const many = r.tags.length > AUDIO_TABLE_TAGS_SHOWN;
                        const open = tagsOpen.has(r.id);
                        const shown = many && !open ? r.tags.slice(0, AUDIO_TABLE_TAGS_SHOWN - 1) : r.tags;
                        return (
                          <>
                            {shown.map((t) => <span key={t} className="chip cat-chip tag-chip" title={t}>{t}</span>)}
                            {many && (
                              <button type="button" className="chip cat-chip tag-chip tags-more" aria-expanded={open} onClick={() => setTagsOpen((o) => {
                                const next = new Set(o);
                                if (next.has(r.id)) next.delete(r.id);
                                else next.add(r.id);
                                return next;
                              })}>{open ? 'Less' : `More (${r.tags.length - shown.length})`}</button>
                            )}
                          </>
                        );
                      })()}
                    </span>
                    {r.mine && (
                      <button type="button" className="name-edit" title="Edit tags" aria-label={`Edit tags for ${r.name}`} onClick={() => setTagging(r)}>
                        <Icon name="pencil" />
                      </button>
                    )}
                  </span>
                </td>
                {showCats && cats.map((c) => (
                  <td key={c.id} className="cat-cell">
                    <span className="row cat-values">
                      <span className="cat-chips">
                        {(r.categories[c.id] ?? []).map((v) => <span key={v.id} className={`chip cat-chip${v.auto ? ' auto' : ''}`} title={v.auto ? `${v.label}: filed automatically` : v.label}>{v.label}</span>)}
                      </span>
                      {r.mine && (
                        <button type="button" className="name-edit" title={`Set ${c.label}`} aria-label={`Set ${c.label} for ${r.name}`} onClick={() => setEditing({ row: r, facetId: c.id })}>
                          <Icon name="pencil" />
                        </button>
                      )}
                    </span>
                  </td>
                ))}
                {route === 'music' && (
                  <>
                    <td className="sub">{r.artist ?? ''}</td>
                    {(['genres', 'moods'] as const).map((field) => (
                      <td key={field} className="cat-cell">
                        <span className="row cat-values">
                          <span className="cat-chips">{r[field].map((g) => <span key={g} className="chip cat-chip" title={g}>{g}</span>)}</span>
                          {r.mine && (
                            <button type="button" className="name-edit" title={field === 'genres' ? 'Set genres' : 'Set moods'} aria-label={`Set ${field} for ${r.name}`} onClick={() => setEditing({ row: r, music: field })}>
                              <Icon name="pencil" />
                            </button>
                          )}
                        </span>
                      </td>
                    ))}
                    <td>
                      <input type="checkbox" aria-label={`Trending: ${r.name}`} checked={r.trending} disabled={!r.mine}
                        onChange={(e) => updateBed.mutate({ bedId: r.id, trending: e.target.checked })} />
                    </td>
                  </>
                )}
                {route === 'voice' && <td className="sub">{r.artist ?? ''}</td>}
                {route === null && <td className="sub">{r.kind === 'sfx' ? 'Effect' : r.kind === 'voice' ? 'Voiceover' : 'Music'}</td>}
                <td className="sub">
                  {r.pack ? <button type="button" className="link-btn pack-link" onClick={() => onOpenPack(r.pack!.id)}>{r.pack.name}</button> : ''}
                </td>
                {route !== 'voice' && <td className="sub">{r.publisher ?? (route === 'music' ? r.artist ?? '' : '')}</td>}
                <td className="sub n nowrap">{fmtWhen(r.createdAt)}</td>
                <td className="menu-cell">
                  <RowMenu title="Sound actions" items={[
                    { label: playingId === r.id ? 'Stop' : 'Play', onClick: () => toggle(r.id, r.audioUrl) },
                    ...(r.pack ? [{ label: 'Open its pack', onClick: () => onOpenPack(r.pack!.id) }] : []),
                    null,
                    { label: 'Analyse and rename', onClick: () => describeBeds.mutate({ bedIds: targetsOf(r), rename: true }) },
                    { label: 'Analyse, keep the name', onClick: () => describeBeds.mutate({ bedIds: targetsOf(r), rename: false }) },
                    ...(r.mine ? [
                      { label: 'Details…', onClick: () => setDetails({
                        id: r.id, name: r.name, kind: r.kind as AudioKind, genres: r.genres as MusicGenre[], moods: r.moods as MusicMood[],
                        sfxCategory: r.sfxCategory as SfxCategory | null, setting: r.setting as SfxSetting | null, trending: r.trending, tags: r.tags, artist: r.artist,
                      }) },
                      null,
                      { label: 'Delete this sound', danger: true, onClick: () => removeBed.mutate({ bedId: r.id }) },
                    ] : []),
                  ]} />
                </td>
              </tr>
            ))}
            {data && rows.length === 0 && <tr><td colSpan={20} className="sub creator-empty">No sounds match.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && 'facetId' in editing && (() => {
        const cat = cats.find((c) => c.id === editing.facetId)!;
        const targets = targetsOf(editing.row);
        return (
          <PickValues title={targets.length > 1 ? `${cat.label} for ${targets.length} sounds` : `${cat.label}: ${editing.row.name}`} options={cat.values}
            initial={(editing.row.categories[cat.id] ?? []).map((v) => v.id)} onClose={() => setEditing(null)}
            onApply={(ids) => { setCategory.mutate({ bedIds: targets, facetId: cat.id, valueIds: ids }); setEditing(null); }} />
        );
      })()}
      {editing && 'music' in editing && (
        <PickValues title={`${editing.music === 'genres' ? 'Genres' : 'Moods'}: ${editing.row.name}`} max={3}
          options={(editing.music === 'genres' ? MUSIC_GENRES : MUSIC_MOODS).map((g) => ({ id: g, label: g }))} initial={editing.row[editing.music]}
          onClose={() => setEditing(null)}
          onApply={(ids) => {
            updateBed.mutate(editing.music === 'genres' ? { bedId: editing.row.id, genres: ids as MusicGenre[] } : { bedId: editing.row.id, moods: ids as MusicMood[] });
            setEditing(null);
          }} />
      )}
      {tagging && <TagsModal name={tagging.name} tags={tagging.tags} onClose={() => setTagging(null)} onSave={(tags) => updateBed.mutate({ bedId: tagging.id, tags })} />}
      {details && <AudioDetails initial={details} credit={null} onClose={() => setDetails(null)} onSaved={refresh} />}
    </div>
  );
}
